import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import EventGalleryImageCard from '../components/EventGalleryImageCard';
import GalleryLightbox from '../components/GalleryLightbox';
import styles from './EventGalleryPage.module.css';
import { useTranslation } from 'react-i18next';

const EventGalleryPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('eventGallery');
  const [eventName, setEventName] = useState('');
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // イベントが変わったらページ番号をリセット
  useEffect(() => {
    setPage(1);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.allSettled([
      axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/gallery`, {
        params: {
          page,
        },
      }),
      axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/my-images`, { withCredentials: true }),
    ])
      .then(([publicResult, privateResult]) => {
        if (cancelled) return;

        if (publicResult.status === 'rejected') {
          setImages([]);
          setLastPage(1);
          const status = publicResult.reason?.response?.status;
          setError(status === 404 ? t('errDraft') : t('errFetch'));
          return;
        }

        const publicRes = publicResult.value;
        const publicData = Array.isArray(publicRes?.data?.data) ? publicRes.data.data : [];

        // 未ログインや認証エラーの場合はマイ画像なし扱い
        const myData = privateResult.status === 'fulfilled'
          ? (Array.isArray(privateResult.value?.data?.data) ? privateResult.value.data.data : [])
          : [];

        // 自分の画像IDリスト（重複除去用）
        const myIds = myData.map(img => img.id);
        const othersApproved = publicData.filter(img => img.status === 'approved' && !myIds.includes(img.id));
        setImages([...myData, ...othersApproved]);
        setLastPage(publicRes.data.meta ? (publicRes.data.meta.last_page || 1) : 1);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, page]);

  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_API_URL}/events/${id}`)
      .then((res) => {
        if (!active) return;
        setEventName(res?.data?.event?.name || '');
      })
      .catch(() => {
        if (!active) return;
        setEventName('');
      });

    return () => {
      active = false;
    };
  }, [id]);

  const openLightbox = (items, index) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setLightboxImages(items);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* ページヘッダー */}
        <div className={styles.pageHeader}>
          <h1 className={styles.heading}>{t('heading')}</h1>
          {eventName && (
            <p className={styles.eventName}>{t('eventNameLabel', { name: eventName })}</p>
          )}
        </div>

        {/* エラー・ローディング */}
        {error && <p className={styles.errorMsg}>{error}</p>}
        {loading && <p className={styles.stateText}>{t('loading')}</p>}

        {/* ギャラリー */}
        {images.length > 0 && (
          <section className={styles.card}>
            <div className={styles.grid}>
              {images.map((img, idx) => (
                <EventGalleryImageCard
                  key={img.id}
                  image={img}
                  onImageClick={() => openLightbox(images, idx)}
                />
              ))}
            </div>

            {/* ページネーション */}
            {lastPage > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t('prevPage')}
                </button>
                <span className={styles.pageInfo}>{page} / {lastPage}</span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= lastPage || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('nextPage')}
                </button>
              </div>
            )}
          </section>
        )}

        {/* 画像なし */}
        {!loading && !error && images.length === 0 && (
          <div className={styles.card}>
            <p className={styles.emptyMsg}>{t('empty')}</p>
          </div>
        )}
      </div>

      <GalleryLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default EventGalleryPage;
